import React, { useState } from "react";

import SmartImage from "../../../components/SmartImage";
import { ConfirmDialog } from "../../../components/SyDialog";
import { formatAmount } from "../../accessories/accessoryMoney";
import { lineKey } from "../cartLines";
import CartLineRow from "./CartLineRow";

const initials = (name) =>
  (name || "S")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

const SupplierCartCard = ({ cart, onChangeQuantity, onRemoveLine, onRemoveCart }) => {
  const [confirmingRemoveAll, setConfirmingRemoveAll] = useState(false);
  const [confirmingRemoveOos, setConfirmingRemoveOos] = useState(false);

  const cylinderRows = [...cart.cylinderLines, ...cart.customLines];
  const showGroupLabels = cylinderRows.length > 0 && cart.accessoryLines.length > 0;
  const itemLabel = `${cart.itemCount} ${cart.itemCount === 1 ? "item" : "items"}`;

  const renderLines = (lines, disabled) =>
    lines.map((line) => (
      <CartLineRow
        key={lineKey(line)}
        line={line}
        disabled={disabled}
        onChangeQuantity={onChangeQuantity}
        onRemove={onRemoveLine}
      />
    ));

  return (
    <section
      className={`sy-flow-card sy-cart-supplier${cart.supplierOnline ? "" : " is-offline"}`}
    >
      <header className="sy-cart-supplier-head">
        <span className="sy-cart-logo">
          {cart.supplierLogoUrl ? (
            <SmartImage src={cart.supplierLogoUrl} alt="" width="100%" height="100%" />
          ) : (
            <span aria-hidden="true">{initials(cart.supplierName)}</span>
          )}
        </span>

        <div className="sy-cart-supplier-body">
          <h3 className="sy-cart-supplier-name">
            <span>{cart.supplierName || "Supplier"}</span>

            {!cart.supplierOnline && (
              <span className="sy-flow-pill sy-flow-pill-warn">Offline</span>
            )}
          </h3>

          <span className="sy-cart-supplier-meta">
            {cart.total !== null
              ? `${itemLabel} · ${formatAmount(cart.total, cart.currency)}`
              : itemLabel}
          </span>
        </div>

        <button
          type="button"
          className="sy-flow-link"
          onClick={() => setConfirmingRemoveAll(true)}
        >
          Remove all
        </button>
      </header>

      {cylinderRows.length > 0 && (
        <>
          {showGroupLabels && <p className="sy-cart-group">Cylinders</p>}
          <ul className="sy-cart-lines">{renderLines(cylinderRows, false)}</ul>
        </>
      )}

      {cart.accessoryLines.length > 0 && (
        <>
          {showGroupLabels && <p className="sy-cart-group">Accessories</p>}
          <ul className="sy-cart-lines">{renderLines(cart.accessoryLines, false)}</ul>
        </>
      )}

      {cart.outOfStockLines.length > 0 && (
        <div className="sy-cart-oos">
          <div className="sy-cart-oos-head">
            <p>Out of stock</p>

            <button
              type="button"
              className="sy-cart-oos-remove"
              onClick={() => setConfirmingRemoveOos(true)}
            >
              {cart.outOfStockLines.length === 1 ? "Remove item" : "Remove them"}
            </button>
          </div>

          <ul className="sy-cart-lines">{renderLines(cart.outOfStockLines, true)}</ul>
        </div>
      )}

      {confirmingRemoveOos && (
        <ConfirmDialog
          title="Remove out-of-stock items?"
          message={
            cart.outOfStockLines.length === 1
              ? `${cart.outOfStockLines[0].name || "This item"} will be removed from your cart.`
              : `${cart.outOfStockLines.length} out-of-stock items will be removed from your cart.`
          }
          confirmText="Remove"
          onCancel={() => setConfirmingRemoveOos(false)}
          onConfirm={() => {
            setConfirmingRemoveOos(false);
            onRemoveCart({ ...cart, lines: cart.outOfStockLines });
          }}
        />
      )}

      {confirmingRemoveAll && (
        <ConfirmDialog
          title="Remove all items?"
          message={`Everything from ${cart.supplierName || "this supplier"} will be removed from your cart.`}
          confirmText="Remove all"
          onCancel={() => setConfirmingRemoveAll(false)}
          onConfirm={() => {
            setConfirmingRemoveAll(false);
            onRemoveCart(cart);
          }}
        />
      )}
    </section>
  );
};

export default SupplierCartCard;
